import useFeatureAd from '@/modules/featureAd/useFeatureAd'
import helpers from '@/modules/message/helpers'
import api from '@/modules/product/api'
import useTopi from '@/modules/topi/useTopi'
import { t } from '@/plugins/i18n.ts'
import { FullProduct, ProductType } from '@/types/index'
import { RemovableRef, useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

interface ProductComparisonState {
  products: RemovableRef<FullProduct[]>
  groups: RemovableRef<ComparisonGroup[]>
  productIdsToLoad: RemovableRef<number[]>
}

export type ComparisonGroup = {
  name: string
  categories: string[]
}

const initialState = (): ProductComparisonState => {
  return {
    products: useStorage('comparisonProducts', []),
    groups: useStorage('comparisonGroups', []),
    productIdsToLoad: useStorage('comparisonProductIds', [])
  }
}

export const useProductComparison = defineStore('productComparison', {
  state: () => initialState(),
  getters: {
    inComparison: (state) => (productId: number) => state.productIdsToLoad.includes(productId),
    count: (state) => state.productIdsToLoad.length,
    currentCategoryID: (state) => {
      const first = state.products[0]
      if (first) {
        const firstProductType = first.productType.id
        if (state.products.every((elem) => elem.productType.id === firstProductType)) {
          return firstProductType
        }
        return 'INVALID'
      }
      return undefined
    }
  },
  actions: {
    async loadProducts(addTopiItems = true) {
      if (this.productIdsToLoad.length == 0) {
        return
      }
      return api.loadComparableProducts(this.productIdsToLoad).then(({ data }) => {
        if (addTopiItems) {
          useTopi().setCartItems(data)
        }
        this.products = data

        const comparisonGroups: ComparisonGroup[] = []
        for (const product of this.products) {
          product.technicalDetails.forEach((technicalDetailToAdd) => {
            const indexOfExistingGroup = comparisonGroups.findIndex(
              (element) => element.name == technicalDetailToAdd.group
            )
            if (indexOfExistingGroup >= 0) {
              if (
                !comparisonGroups[indexOfExistingGroup].categories.some(
                  (el) => el == technicalDetailToAdd.name
                )
              ) {
                comparisonGroups[indexOfExistingGroup].categories.push(technicalDetailToAdd.name)
              }
            } else {
              comparisonGroups.push({
                name: technicalDetailToAdd.group,
                categories: [technicalDetailToAdd.name]
              })
            }
          })
        }

        this.groups = comparisonGroups
      })
    },
    addProduct(productId: number, productTyoe: ProductType) {
      if (this.productIdsToLoad.length == 3) {
        helpers.reportError(t('error.productComparisonMaxNumber'))
        return
      }

      if (this.productIdsToLoad.some((elem) => elem == productId)) {
        helpers.reportError(t('error.productAlreadyInComparison'))
        return
      }
      if (this.count > 0 && this.currentCategoryID !== productTyoe.id) {
        helpers.reportError(t('error.productTypeIncorrectForComparison'))
        return
      }

      useFeatureAd().hideFeatureAd()

      this.productIdsToLoad.push(productId)
      this.loadProducts(false)
      helpers.reportSuccess(t('success.addedToComparison'))
    },
    removeProduct(productId: number) {
      this.productIdsToLoad = this.productIdsToLoad.filter((prod) => prod != productId)

      this.products = this.products.filter((prod) => prod.productId != productId)
      helpers.reportSuccess(t('success.removedFromComparison'))
    },
    reset() {
      this.productIdsToLoad = []
      this.groups = []
      this.products = []
    }
  }
})

export default useProductComparison
